import { useCallback, useMemo } from 'react'

import { useConfig } from 'wagmi'
import { getTransactionCount } from 'wagmi/actions'

import { useWalletInfo, useIsSafeWallet } from '@cowprotocol/wallet'

import { useAllTransactions } from './TransactionHooksMod'

import { useAppDispatch } from '../../hooks'
import { addTransaction, AddTransactionParams } from '../actions'
import { EnhancedTransactionDetails, HashType } from '../reducer'

export * from './TransactionHooksMod'

export type AddTransactionHookParams = Omit<AddTransactionParams, 'chainId' | 'from' | 'hashType' | 'nonce'> // The hook requires less params for convenience
export type TransactionAdder = (params: AddTransactionHookParams) => void

type EnhancedTransactionDetailsMap = {
  [txHash: string]: EnhancedTransactionDetails
}

type TransactionFilter = (tx: EnhancedTransactionDetails) => boolean

/**
 * Return all transaction hashes
 */
export function useAllTransactionHashes(filter?: TransactionFilter): string[] {
  const transactions = useAllTransactionsDetails(filter)

  return useMemo(() => transactions.map((tx) => tx.hash), [transactions])
}

/**
 * Return all transactions details
 */
export function useAllTransactionsDetails(filter?: TransactionFilter): EnhancedTransactionDetails[] {
  const transactions = useAllTransactions()

  return useMemo(() => {
    const transactionsDetails = Object.keys(transactions).map((hash) => transactions[hash])

    return filter ? transactionsDetails.filter(filter) : transactionsDetails
  }, [transactions, filter])
}

/**
 * Return helpers to add a new pending transaction
 */
export function useTransactionAdder(): TransactionAdder {
  const config = useConfig()
  const { chainId, account } = useWalletInfo()
  const dispatch = useAppDispatch()
  const isSafeWallet = useIsSafeWallet()
  const allTxs = useAllTransactions()

  const maxPendingNonce = useMemo(() => {
    const nonces = Object.values(allTxs).map((tx) => tx.nonce)
    return nonces.length > 0 ? Math.max(...nonces) : -1
  }, [allTxs])

  return useCallback(
    async (addTransactionParams: AddTransactionHookParams) => {
      if (!account) return

      const hashType = isSafeWallet ? HashType.GNOSIS_SAFE_TX : HashType.ETHEREUM_TX

      if (!addTransactionParams.hash) {
        throw Error('No transaction hash found')
      }

      try {
        // Use 'pending' so the next tx gets the next nonce when multiple txs are sent in quick succession (e.g. wrap then unwrap).
        // Also account for our own pending txs in case the node hasn't seen them yet.
        const chainNonce = await getTransactionCount(config, { address: account, blockTag: 'pending' })
        const nonce = Math.max(chainNonce, maxPendingNonce + 1)

        dispatch(
          addTransaction({
            hashType,
            from: account,
            chainId,
            ...addTransactionParams,
            nonce,
          }),
        )
      } catch (e) {
        console.error('Cannot add a transaction', e)
      }
    },
    [dispatch, chainId, account, isSafeWallet, config, maxPendingNonce],
  )
}

export function useTransactionsByHash({ hashes }: { hashes: string[] }): EnhancedTransactionDetailsMap {
  const allTxs = useAllTransactions()

  return useMemo(() => {
    if (!allTxs || !hashes) {
      return {}
    }

    return hashes.reduce<EnhancedTransactionDetailsMap>((acc, hash) => {
      if (allTxs[hash]) {
        acc[hash] = allTxs[hash]
      }
      return acc
    }, {})
  }, [allTxs, hashes])
}
